import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { House, Coffee, Briefcase, CalendarCheck, Bandaids, UserMinus, MonitorPlay, SignIn } from "@phosphor-icons/react";

const GROUP_ORDER = ["ARRIVED", "WFH", "BREAK", "BUSINESS_TRIP", "ANNUAL", "SICK", "UNPAID", "ABSENT"];

const GROUP_META = {
  ARRIVED: { labelKey: "in_office", bg: "bg-[#00CC33]", icon: SignIn },
  WFH: { labelKey: "wfh", bg: "bg-[#FF66B2]", icon: House },
  BREAK: { labelKey: "on_break", bg: "bg-[#FFCC00]", icon: Coffee },
  BUSINESS_TRIP: { labelKey: "business_trip", bg: "bg-[#FF8000]", icon: Briefcase },
  ANNUAL: { labelKey: "annual", bg: "bg-[#0047FF] text-white", icon: CalendarCheck },
  SICK: { labelKey: "sick", bg: "bg-[#FF3333] text-white", icon: Bandaids },
  UNPAID: { labelKey: "unpaid", bg: "bg-black text-white", icon: UserMinus },
  ABSENT: { labelKey: "absent", bg: "bg-white", icon: UserMinus },
};

export default function Monitor() {
  const { t } = useI18n();
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/presence/monitor");
      setGroups(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const now = new Date();
  const clock = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-black/60 flex items-center gap-2">
            <MonitorPlay size={14} weight="bold" /> {t("monitor")}
          </div>
          <h1 className="font-display font-black text-5xl tracking-tighter leading-none" data-testid="monitor-title">
            Monitor · {clock}
          </h1>
          <div className="text-xs font-mono text-black/60 mt-1">
            {now.toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </div>
        </div>
        <div className="text-xs font-mono text-black/60">Auto-refresh 15s</div>
      </div>

      {loading && !groups && <div className="brut-border bg-white p-8 text-center">Loading…</div>}

      {groups && (
        <div className="space-y-6">
          {GROUP_ORDER.map((key) => {
            const list = groups[key] || [];
            if (!list.length && key !== "ARRIVED" && key !== "ABSENT") return null;
            const meta = GROUP_META[key];
            const Icon = meta.icon;
            return (
              <div key={key} className="brut-border bg-white brut-shadow">
                <div className={`flex items-center justify-between p-3 border-b-2 border-black ${meta.bg}`}>
                  <div className="flex items-center gap-2 font-display font-black text-lg tracking-tighter uppercase">
                    <Icon size={20} weight="bold" />
                    {t(meta.labelKey)}
                  </div>
                  <div className="tag bg-white text-black" data-testid={`monitor-count-${key.toLowerCase()}`}>{list.length}</div>
                </div>
                <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {list.map((c) => (
                    <div
                      key={c.user.id}
                      className="brut-border p-3 flex flex-col items-start gap-1 hover:bg-[#F4F4F0] transition-colors"
                      data-testid={`monitor-card-${c.user.id}`}
                    >
                      <div className="font-mono text-xs font-bold">{c.arrival_time || "--:--"}</div>
                      <div className="font-bold uppercase text-sm leading-tight tracking-tight">{c.user.name}</div>
                      <div className="text-[10px] font-mono text-black/60 truncate w-full">{c.user.team || "—"}</div>
                    </div>
                  ))}
                  {list.length === 0 && (
                    <div className="col-span-full text-center text-black/40 text-xs py-3">—</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
